import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FeeDataType } from "src/lib/constants/table-type";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import {
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Asset, conversionDetails, ParamsSort } from "src/api/types";
import { CheckIcon } from "src/components/icons/check-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { useModalAction } from "src/components/modal-views/context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import CurrencyDrop from "src/components/ui/dropdown-list";
import { Form, Input, InputNumber, Table } from "antd";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: FeeDataType;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const Conversion = () => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record: conversionDetails) => record.key === editingKey;

  const { canView, canList, canEdit } = usePermissions(
    permissionConstants.CONVERSION
  );
  const canViewConversion = canView || canList;

  const [dataSource, setDataSource] = useState<conversionDetails[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const { openModal, closeModal } = useModalAction();
  // const [searchParams, setSearchParams] = useState<ParamsSort>({
  //   asset_code: "",
  //   channel_name: "",
  // });

  const [error, setError] = useState<any>();
  const isMounted = useIsMounted();
  const [updatedData, setUpdatedData] = useState<conversionDetails | null>(
    null,
  );
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency Sell
        </p>
      ),
      dataIndex: "sellAsset",
      key: "sellAsset",
      className: "w-auto",
      editable: false,
      render: (value: Asset) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value?.code?.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency Buy
        </p>
      ),
      dataIndex: "buyAsset",
      key: "buyAsset",
      className: "w-auto",
      editable: false,
      render: (value: Asset) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value?.code?.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          General Spread
        </p>
      ),
      dataIndex: "general_spread",
      key: "general_spread",
      className: "w-auto",
      editable: true,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          VIP Spread
        </p>
      ),
      dataIndex: "vip_spread",
      key: "vip_spread",
      className: "w-auto",
      editable: true,
      render: (value: string) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Range
        </p>
      ),
      dataIndex: "range",
      key: "range",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Single Limit
        </p>
      ),
      dataIndex: "single_limit",
      key: "single_limit",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Daily Limit
        </p>
      ),
      dataIndex: "daily_limit",
      key: "daily_limit",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          General Convert Fee %
        </p>
      ),
      dataIndex: "general_fee_percent",
      key: "general_fee_percent",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          VIP Convert Fee %
        </p>
      ),
      dataIndex: "vip_fee_percent",
      key: "vip_fee_percent",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          General BIPS
        </p>
      ),
      dataIndex: "general_bips",
      key: "general_bips",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          VIP BIPS
        </p>
      ),
      dataIndex: "vip_bips",
      key: "vip_bips",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_: any, record: conversionDetails) => {
        if (!canEdit) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        const editable = isEditing(record);
        return editable ? (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => cancel(record)}
            >
              <CloseIcon className="w-[20px] h-[20px] text-secondary" />
            </button>
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={
                () => save(record.key)
                // record?.isNew ? addData(record) : save(record.key)
              }
            >
              <CheckIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            {/* <button
              className="w-[30px] h-[30px] flex items-center justify-center"
              onClick={() => onDelete(record.key as number)}
            >
              <DeleteIcon className="w-[20px] h-[20px] text-primary" />
            </button> */}
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => edit(record)}
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: conversionDetails) => ({
        record,
        inputType: col.dataIndex === "range" ? "text" : "number", // 👈 Fix here
        dataIndex: col.dataIndex,
        title: "",
        editing: isEditing(record),
      }),
    };
  });

  const mobileColumns: any = [];

  const { mutate: getList, isLoading } = useMutation(client.conversions.all, {
    onSuccess: (data) => {
      setDataSource(
        data.items.map((item: any) => ({
          key: item.id,
          range: String(item.range_start) + "-" + String(item.range_end),
          ...item,
        })),
      );
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: updateConversion, isLoading: updateConversionLoading } =
    useMutation(client.conversions.update, {
      onSuccess: (data) => {
        if (updatedData) {
          const newData = [...dataSource];
          const index = newData.findIndex((item) => editingKey == item.key);
          if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
              ...item,
              ...updatedData!,
            });
            setDataSource(newData);
            setEditingKey("");
          } else {
            newData.push(updatedData!);
            setDataSource(newData);
            setEditingKey("");
          }
        } else {
          setEditingKey("");
        }
        getList();
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: exportLimitations, isLoading: exportListLoading } =
    useMutation(client.fee.export, {
      onSuccess: (data) => {
        logger.log(data);
        setAlertText("Export success");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const edit = (record: Partial<conversionDetails> & { key: React.Key }) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key as string);
  };

  const cancel = (record: any) => {
    setEditingKey("");
    form.resetFields();
    if (record.isNew) {
      setDataSource((prev) => prev.filter((item) => item.key !== record.key));
    }
  };

  const save = async (key: React.Key) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit conversion rates");
      setIsAlertOpened(true);
      return;
    }
    try {
      const row = (await form.validateFields()) as conversionDetails;
      setUpdatedData(row);
      logger.log(row, "ROWWW");
      const startRange = row?.range.split("-")[0];
      const endRange = row?.range.split("-")[1];
      setTimeout(() => {
        const { range, ...payload } = row;

        updateConversion({
          ...payload,
          range_start: Number(startRange),
          range_end: Number(endRange),
          id: key as number,
        });
      }, 300);
      return;
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };
  // const onCallback = (bSuccess: boolean) => {
  //   bSuccess && getList(searchParams);
  // };

  // const onDelete = (key: number) => {
  //   openModal("FEE_DELETE_CONFIRM_VIEW", {
  //     id: key,
  //     callback: onCallback,
  //   });
  // };

  const editOnMobile = (key: number) => {
    const selData = dataSource.filter((item) => item.key === key)[0];
    openModal("UPDATE_FEE_VIEW", { ...selData, callback: onCallBack_1 });
  };

  const onCallBack_1 = (data: any) => {
    logger.log(data);
    setEditingKey(String(data.id));
    setUpdatedData({ key: Number(data.id), ...data });
  };

  const exportBtn = async () => {
    try {
      setTimeout(() => {
        // exportLimitations();
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  useEffect(() => {
    if (!canViewConversion) return;
    getList();
  }, [canViewConversion]);

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col p-2 sm:p-4">
        <div className="title-block flex mb-16">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Conversion Management
          </p>
        </div>
        {!canViewConversion ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view conversion management.
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            {isMobile ? (
              <Table
                loading={isLoading}
                showSorterTooltip={false}
                columns={mobileColumns}
                dataSource={dataSource}
                pagination={false}
              />
            ) : (
              <Form form={form} component={false}>
                <div className="custom-scroll">
                  <Table
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    loading={isLoading}
                    showSorterTooltip={false}
                    columns={mergedColumns}
                    dataSource={dataSource}
                    pagination={false}
                    rowClassName="editable-row"
                  />
                </div>
              </Form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversion;
